import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Archive, House, FileText, User, ArrowCounterClockwise, ArrowRight, Trash, Warning, Bank,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { formatDate, formatEUR } from "@/lib/constants";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Zentrales Archiv – zeigt alle archivierten/soft-gelöschten Entitäten.
 * Verwalter kann diese wiederherstellen. Admins können sie endgültig löschen.
 */
export default function Archiv() {
  const { user } = useAuth();
  const isAdmin = ["super_admin", "admin"].includes(user?.role);
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "tenancies";
  const [counts, setCounts] = useState({ tenancies: 0, properties: 0, documents: 0, contacts: 0, bank_transactions: 0, total: 0 });

  const loadCounts = async () => {
    try {
      const { data } = await api.get("/archive/summary");
      setCounts(data);
    } catch {
      setCounts({ tenancies: 0, properties: 0, documents: 0, contacts: 0, bank_transactions: 0, total: 0 });
    }
  };

  useEffect(() => { loadCounts(); }, []);

  return (
    <div className="space-y-5" data-testid="archive-page">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center shrink-0">
          <Archive size={24} className="text-slate-600" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Archiv</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Archivierte Mietverhältnisse und gelöschte Datensätze. Wiederherstellung jederzeit möglich.
            {isAdmin && <span className="ml-1 text-slate-600">Admins können Datensätze endgültig löschen — Nummern werden dann wieder frei.</span>}
          </p>
        </div>
        <div className="ml-auto text-right">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Gesamt</div>
          <div className="text-2xl font-semibold tabular-nums text-slate-900">{counts.total}</div>
        </div>
      </div>

      <Tabs defaultValue={initialTab} className="space-y-4">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="tenancies" data-testid="tab-archive-tenancies">
            Mietverhältnisse <span className="ml-1.5 text-[10px] bg-white text-slate-700 px-1.5 rounded-full">{counts.tenancies}</span>
          </TabsTrigger>
          <TabsTrigger value="properties" data-testid="tab-archive-properties">
            Objekte <span className="ml-1.5 text-[10px] bg-white text-slate-700 px-1.5 rounded-full">{counts.properties}</span>
          </TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-archive-documents">
            Dokumente <span className="ml-1.5 text-[10px] bg-white text-slate-700 px-1.5 rounded-full">{counts.documents}</span>
          </TabsTrigger>
          <TabsTrigger value="contacts" data-testid="tab-archive-contacts">
            Kontakte <span className="ml-1.5 text-[10px] bg-white text-slate-700 px-1.5 rounded-full">{counts.contacts}</span>
          </TabsTrigger>
          <TabsTrigger value="bank" data-testid="tab-archive-bank">
            Bankbuchungen <span className="ml-1.5 text-[10px] bg-white text-slate-700 px-1.5 rounded-full">{counts.bank_transactions || 0}</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="tenancies">
          <ArchivedTenancies onChange={loadCounts} isAdmin={isAdmin} />
        </TabsContent>
        <TabsContent value="properties">
          <ArchivedProperties onChange={loadCounts} isAdmin={isAdmin} />
        </TabsContent>
        <TabsContent value="documents">
          <ArchivedDocuments onChange={loadCounts} isAdmin={isAdmin} />
        </TabsContent>
        <TabsContent value="contacts">
          <ArchivedContacts onChange={loadCounts} isAdmin={isAdmin} />
        </TabsContent>
        <TabsContent value="bank">
          <ArchivedBankTransactions onChange={loadCounts} isAdmin={isAdmin} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ====================== Shared Hard-Delete Dialog =====================

function HardDeleteDialog({ open, onClose, title, warning, itemLabel, confirmToken, onConfirm, entity }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (!open) setText(""); }, [open]);
  const canDelete = text.trim() === confirmToken;

  const submit = async () => {
    setBusy(true);
    try {
      await onConfirm();
      onClose();
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid={`hard-delete-dialog-${entity}`}>
        <DialogHeader>
          <div className="w-11 h-11 rounded-full bg-rose-100 flex items-center justify-center mb-2">
            <Warning size={22} className="text-rose-700" weight="fill" />
          </div>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription asChild>
            <div className="text-sm text-slate-700 space-y-2">
              <div>{itemLabel}</div>
              <div className="bg-rose-50 border border-rose-200 rounded p-2.5 text-[13px] text-rose-900">
                {warning}
              </div>
              <div>
                Zur Bestätigung tippe bitte <span className="font-mono font-semibold text-slate-900">{confirmToken}</span> in das Feld ein.
              </div>
            </div>
          </DialogDescription>
        </DialogHeader>
        <Input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={confirmToken}
          className="font-mono"
          autoFocus
          data-testid={`hard-delete-confirm-input-${entity}`}
        />
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Abbrechen</Button>
          <Button
            className="bg-rose-700 hover:bg-rose-800 text-white"
            onClick={submit}
            disabled={!canDelete || busy}
            data-testid={`hard-delete-confirm-btn-${entity}`}
          >
            <Trash size={14} weight="fill" className="mr-1.5" />
            {busy ? "Lösche…" : "Endgültig löschen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ====================== Tenancies =====================

function ArchivedTenancies({ onChange, isAdmin }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hardDelTarget, setHardDelTarget] = useState(null);
  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/archive/tenancies"); setItems(data || []); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const restore = async (t) => {
    if (!await window._domivusConfirm(`Mietverhältnis ${t.contract_number} wiederherstellen?`)) return;
    try {
      const { data } = await api.post(`/archive/tenancies/${t.id}/restore`);
      toast.success(`Wiederhergestellt (Status: ${data.new_status})`);
      load(); onChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler beim Wiederherstellen"); }
  };
  const hardDelete = async () => {
    if (!hardDelTarget) return;
    try {
      const { data } = await api.delete(`/archive/tenancies/${hardDelTarget.id}/hard-delete`);
      toast.success(`Endgültig gelöscht. Vertragsnummer ${data.contract_number_freed} ist wieder frei.`);
      load(); onChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler beim endgültigen Löschen"); }
  };

  if (loading) return <Empty>Lade…</Empty>;
  if (items.length === 0) return <Empty>Keine archivierten Mietverhältnisse.</Empty>;

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 border-b border-slate-100">
          <tr>
            <Th className="text-left">Vertrags-Nr.</Th>
            <Th className="text-left">Objekt / Einheit</Th>
            <Th className="text-left">Mieter</Th>
            <Th className="text-left">Vertragsbeginn</Th>
            <Th className="text-right">Kaltmiete</Th>
            <Th className="text-right w-56">Aktionen</Th>
          </tr>
        </thead>
        <tbody>
          {items.map((t) => (
            <tr key={t.id} className="border-t border-slate-100 hover:bg-slate-50/70" data-testid={`archive-tenancy-${t.id}`}>
              <Td className="font-mono text-xs">{t.contract_number}</Td>
              <Td>
                <div>{t.property_name || "—"}</div>
                <div className="text-[11px] font-mono text-slate-500">{t.unit_label}</div>
              </Td>
              <Td>{(t.tenant_names || []).join(", ") || "—"}</Td>
              <Td className="tabular-nums text-xs">{formatDate(t.contract_start)}</Td>
              <Td className="tabular-nums">{formatEUR(t.rent_cold)}</Td>
              <Td className="text-right">
                <div className="flex justify-end gap-1.5">
                  <Button size="sm" variant="outline" className="h-8 gap-1.5" onClick={() => restore(t)} data-testid={`restore-tenancy-${t.id}`}>
                    <ArrowCounterClockwise size={14} /> Wiederherstellen
                  </Button>
                  {isAdmin && (
                    <Button
                      size="sm" variant="outline"
                      className="h-8 gap-1.5 text-rose-700 border-rose-200 hover:bg-rose-50 hover:text-rose-800"
                      onClick={() => setHardDelTarget(t)}
                      data-testid={`hard-delete-tenancy-${t.id}`}
                      title="Endgültig löschen (Admin)"
                    >
                      <Trash size={14} weight="fill" /> Endgültig löschen
                    </Button>
                  )}
                </div>
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
      <HardDeleteDialog
        entity="tenancy"
        open={!!hardDelTarget}
        onClose={() => setHardDelTarget(null)}
        title="Mietverhältnis endgültig löschen?"
        itemLabel={hardDelTarget && (
          <>Vertragsnummer <span className="font-mono font-semibold text-slate-900">{hardDelTarget.contract_number}</span> · {(hardDelTarget.tenant_names || []).join(" & ") || "—"}</>
        )}
        warning="Alle zugehörigen Sollstellungen, Buchungsregeln und Buchungen werden mit gelöscht. Die Vertragsnummer wird sofort wieder frei und kann für neue MV verwendet werden. Diese Aktion kann NICHT rückgängig gemacht werden."
        confirmToken={hardDelTarget?.contract_number || ""}
        onConfirm={hardDelete}
      />
    </div>
  );
}

// ====================== Properties =====================

function ArchivedProperties({ onChange, isAdmin }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hardDelTarget, setHardDelTarget] = useState(null);
  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/archive/properties"); setItems(data || []); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const restore = async (p) => {
    if (!await window._domivusConfirm(`Objekt ${p.property_number || p.name} wiederherstellen?`)) return;
    try {
      await api.post(`/archive/properties/${p.id}/restore`);
      toast.success("Objekt wiederhergestellt");
      load(); onChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
  };
  const hardDelete = async () => {
    if (!hardDelTarget) return;
    try {
      const { data } = await api.delete(`/archive/properties/${hardDelTarget.id}/hard-delete`);
      toast.success(
        `Objekt ${data.property_number_freed} endgültig gelöscht. ${data.units_deleted} Einheit(en), ${data.tenancies_deleted} MV, ${data.postings_deleted} Buchung(en) mit gelöscht.`
      );
      load(); onChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler beim endgültigen Löschen"); }
  };

  if (loading) return <Empty>Lade…</Empty>;
  if (items.length === 0) return <Empty>Keine gelöschten Objekte.</Empty>;

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
      <ul className="divide-y divide-slate-100">
        {items.map((p) => (
          <li key={p.id} className="px-5 py-3 flex items-center gap-3" data-testid={`archive-property-${p.id}`}>
            <House size={16} className="text-slate-400" />
            <div className="flex-1 min-w-0">
              <div className="font-medium text-slate-900 truncate">
                <span className="font-mono text-xs text-slate-500 mr-2">{p.property_number || "—"}</span>
                {p.name}
              </div>
              <div className="text-[11px] text-slate-500">
                {p.address?.street} {p.address?.house_number}, {p.address?.zip} {p.address?.city}
              </div>
            </div>
            <div className="flex gap-1.5">
              <Button size="sm" variant="outline" className="h-8 gap-1.5" onClick={() => restore(p)} data-testid={`restore-property-${p.id}`}>
                <ArrowCounterClockwise size={14} /> Wiederherstellen
              </Button>
              {isAdmin && (
                <Button
                  size="sm" variant="outline"
                  className="h-8 gap-1.5 text-rose-700 border-rose-200 hover:bg-rose-50 hover:text-rose-800"
                  onClick={() => setHardDelTarget(p)}
                  data-testid={`hard-delete-property-${p.id}`}
                  title="Endgültig löschen (Admin)"
                >
                  <Trash size={14} weight="fill" /> Endgültig löschen
                </Button>
              )}
            </div>
          </li>
        ))}
      </ul>
      <HardDeleteDialog
        entity="property"
        open={!!hardDelTarget}
        onClose={() => setHardDelTarget(null)}
        title="Objekt endgültig löschen?"
        itemLabel={hardDelTarget && (
          <>Objektnummer <span className="font-mono font-semibold text-slate-900">{hardDelTarget.property_number}</span> · {hardDelTarget.name}</>
        )}
        warning="Alle Einheiten, Mietverhältnisse, Sollstellungen, Zähler, Versicherungen und Energieausweise dieses Objekts werden unwiderruflich gelöscht. Die Objektnummer wird sofort wieder frei."
        confirmToken={hardDelTarget?.property_number || ""}
        onConfirm={hardDelete}
      />
    </div>
  );
}

// ====================== Documents =====================

function ArchivedDocuments({ onChange, isAdmin }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hardDelTarget, setHardDelTarget] = useState(null);
  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/archive/documents"); setItems(data || []); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const restore = async (d) => {
    if (!await window._domivusConfirm(`Dokument „${d.title}" wiederherstellen?`)) return;
    try {
      await api.post(`/archive/documents/${d.id}/restore`);
      toast.success("Dokument wiederhergestellt");
      load(); onChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
  };
  const hardDelete = async () => {
    if (!hardDelTarget) return;
    try {
      await api.delete(`/archive/documents/${hardDelTarget.id}/hard-delete`);
      toast.success(`Dokument „${hardDelTarget.title}" endgültig gelöscht.`);
      load(); onChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler beim endgültigen Löschen"); }
  };

  if (loading) return <Empty>Lade…</Empty>;
  if (items.length === 0) return <Empty>Keine gelöschten Dokumente.</Empty>;

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
      <ul className="divide-y divide-slate-100">
        {items.map((d) => (
          <li key={d.id} className="px-5 py-3 flex items-center gap-3" data-testid={`archive-doc-${d.id}`}>
            <FileText size={16} className="text-slate-400 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-medium text-slate-900 truncate">{d.title}</div>
              <div className="text-[11px] text-slate-500">
                {d.category} · {formatDate(d.created_at)} · {d.original_filename}
              </div>
            </div>
            <div className="flex gap-1.5">
              <Button size="sm" variant="outline" className="h-8 gap-1.5" onClick={() => restore(d)} data-testid={`restore-doc-${d.id}`}>
                <ArrowCounterClockwise size={14} /> Wiederherstellen
              </Button>
              {isAdmin && (
                <Button
                  size="sm" variant="outline"
                  className="h-8 gap-1.5 text-rose-700 border-rose-200 hover:bg-rose-50 hover:text-rose-800"
                  onClick={() => setHardDelTarget(d)}
                  data-testid={`hard-delete-doc-${d.id}`}
                  title="Endgültig löschen (Admin)"
                >
                  <Trash size={14} weight="fill" /> Endgültig löschen
                </Button>
              )}
            </div>
          </li>
        ))}
      </ul>
      <HardDeleteDialog
        entity="document"
        open={!!hardDelTarget}
        onClose={() => setHardDelTarget(null)}
        title="Dokument endgültig löschen?"
        itemLabel={hardDelTarget && (
          <>Dokument <span className="font-semibold text-slate-900">„{hardDelTarget.title}"</span> ({hardDelTarget.original_filename})</>
        )}
        warning="Das Dokument wird komplett aus dem System entfernt, inkl. der hinterlegten Datei. Es kann anschließend NICHT wieder hergestellt werden."
        confirmToken="LÖSCHEN"
        onConfirm={hardDelete}
      />
    </div>
  );
}

// ====================== Contacts =====================

function ArchivedContacts({ onChange, isAdmin }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hardDelTarget, setHardDelTarget] = useState(null);
  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/archive/contacts"); setItems(data || []); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const restore = async (c) => {
    const name = `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.company_name || c.email;
    if (!await window._domivusConfirm(`Kontakt „${name}" wiederherstellen?`)) return;
    try {
      await api.post(`/archive/contacts/${c.id}/restore`);
      toast.success("Kontakt wiederhergestellt");
      load(); onChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
  };
  const hardDelete = async () => {
    if (!hardDelTarget) return;
    try {
      const { data } = await api.delete(`/archive/contacts/${hardDelTarget.id}/hard-delete`);
      toast.success(`Kontakt „${data.name}" endgültig gelöscht.`);
      load(); onChange?.();
    } catch (e) {
      // 409 Conflict = Kontakt noch referenziert
      toast.error(e?.response?.data?.detail || "Fehler beim endgültigen Löschen");
    }
  };

  if (loading) return <Empty>Lade…</Empty>;
  if (items.length === 0) return <Empty>Keine gelöschten Kontakte.</Empty>;

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
      <ul className="divide-y divide-slate-100">
        {items.map((c) => {
          const name = `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.company_name || c.email;
          return (
            <li key={c.id} className="px-5 py-3 flex items-center gap-3" data-testid={`archive-contact-${c.id}`}>
              <User size={16} className="text-slate-400 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-slate-900 truncate">{name}</div>
                <div className="text-[11px] text-slate-500">{c.email || c.phone || c.mobile || ""}</div>
              </div>
              <div className="flex gap-1.5">
                <Button size="sm" variant="outline" className="h-8 gap-1.5" onClick={() => restore(c)} data-testid={`restore-contact-${c.id}`}>
                  <ArrowCounterClockwise size={14} /> Wiederherstellen
                </Button>
                {isAdmin && (
                  <Button
                    size="sm" variant="outline"
                    className="h-8 gap-1.5 text-rose-700 border-rose-200 hover:bg-rose-50 hover:text-rose-800"
                    onClick={() => setHardDelTarget(c)}
                    data-testid={`hard-delete-contact-${c.id}`}
                    title="Endgültig löschen (Admin)"
                  >
                    <Trash size={14} weight="fill" /> Endgültig löschen
                  </Button>
                )}
              </div>
            </li>
          );
        })}
      </ul>
      <HardDeleteDialog
        entity="contact"
        open={!!hardDelTarget}
        onClose={() => setHardDelTarget(null)}
        title="Kontakt endgültig löschen?"
        itemLabel={hardDelTarget && (
          <>Kontakt <span className="font-semibold text-slate-900">
            {`${hardDelTarget.first_name || ""} ${hardDelTarget.last_name || ""}`.trim() || hardDelTarget.company_name || hardDelTarget.email}
          </span></>
        )}
        warning="Der Kontakt wird komplett aus dem System entfernt. Falls er in aktiven Mietverhältnissen oder Objekten (als Eigentümer) noch referenziert wird, verweigert das System die Löschung."
        confirmToken="LÖSCHEN"
        onConfirm={hardDelete}
      />
    </div>
  );
}

// ====================== Helpers =====================

function Th({ children, className = "" }) {
  return <th className={`px-3 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-600 ${className}`}>{children}</th>;
}
function Td({ children, className = "" }) {
  return <td className={`px-3 py-3 ${className}`}>{children}</td>;
}
function Empty({ children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl py-12 text-center text-slate-500 text-sm">
      <Archive size={36} className="mx-auto mb-2 text-slate-300" />
      {children}
    </div>
  );
}



// ====================== VM-14e — Bankbuchungen (Bank-Archiv) =====================

function ArchivedBankTransactions({ onChange, isAdmin }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/bank/transactions", {
        params: { include_archived: true, limit: 1000 },
      });
      setItems((data || []).filter((t) => t.is_archived));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const restore = async (id) => {
    if (!await window._domivusConfirm('Buchung wiederherstellen? Sie erscheint danach im Bank-Assistenten als "nicht zugeordnet".')) return;
    try {
      await api.post(`/bank/transactions/${id}/restore`);
      toast.success("Wiederhergestellt");
      load();
      onChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
  };
  const permanentDelete = async (id) => {
    if (!await window._domivusConfirm("Endgültig löschen? Das kann nicht rückgängig gemacht werden.")) return;
    try {
      await api.delete(`/bank/transactions/${id}`, { params: { permanent: true } });
      toast.success("Endgültig gelöscht");
      load();
      onChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
  };

  if (loading) return <div className="text-sm text-slate-500 p-4">Lade…</div>;
  if (items.length === 0) return <Empty>Keine archivierten Bankbuchungen.</Empty>;

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="archived-bank-list">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 border-b border-slate-200 text-left">
          <tr>
            <Th>Datum</Th><Th>Auftraggeber</Th><Th>Verwendungszweck</Th>
            <Th className="text-right">Betrag</Th><Th>Archiviert am</Th><Th></Th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {items.map((t) => (
            <tr key={t.id} className="hover:bg-slate-50/40" data-testid={`archived-bank-row-${t.id}`}>
              <Td className="tabular-nums text-xs whitespace-nowrap">{formatDate(t.booking_date)}</Td>
              <Td>{t.counterparty_name || "—"}</Td>
              <Td className="max-w-md truncate text-slate-600">{t.purpose || t.booking_text || "—"}</Td>
              <Td className={`text-right tabular-nums font-medium ${t.amount < 0 ? "text-rose-700" : "text-emerald-700"}`}>{formatEUR(t.amount)}</Td>
              <Td className="text-xs text-slate-500 tabular-nums">{t.archived_at ? formatDate(t.archived_at) : "—"}</Td>
              <Td className="text-right whitespace-nowrap">
                <Button size="sm" variant="ghost" onClick={() => restore(t.id)} data-testid={`archived-bank-restore-${t.id}`}>
                  <ArrowCounterClockwise size={12} className="mr-1" /> Wiederherstellen
                </Button>
                {isAdmin && (
                  <Button size="sm" variant="ghost" className="text-rose-700 hover:text-rose-800 hover:bg-rose-50" onClick={() => permanentDelete(t.id)} data-testid={`archived-bank-delete-${t.id}`}>
                    <Trash size={12} />
                  </Button>
                )}
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
